import { useEffect, useState, type RefObject } from "react";

export interface TocHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

/** Slugify heading text into a stable, URL-safe anchor id. */
function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .trim()
      // keep unicode letters/numbers (handles š, č, ž, ...), spaces and hyphens
      .replace(/[^\p{L}\p{N}\s-]/gu, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "section"
  );
}

interface UseTableOfContentsReturn {
  /** Headings discovered in the rendered content, in document order. */
  headings: TocHeading[];
  /** Id of the heading currently nearest the top of the viewport. */
  activeId: string | null;
}

/**
 * Scans the rendered content container for `h2`/`h3` elements, assigns stable
 * anchor ids to each, and tracks which heading is active via scroll position.
 *
 * The content is produced by `BlogRenderer` (raw HTML via dangerouslySetInnerHTML),
 * so ids are assigned post-render here rather than at render time.
 *
 * @param containerRef ref to the element wrapping the rendered content
 * @param contentKey   value that changes when the rendered content changes
 *                     (e.g. the page id), used to re-scan
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * const { headings, activeId } = useTableOfContents(ref, page?.id);
 */
export function useTableOfContents(
  containerRef: RefObject<HTMLElement | null>,
  contentKey?: string | null,
): UseTableOfContentsReturn {
  const [headings, setHeadings] = useState<TocHeading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setHeadings([]);
      return;
    }

    const nodes = Array.from(
      container.querySelectorAll<HTMLHeadingElement>("h2, h3"),
    );

    const used = new Set<string>();
    const collected: TocHeading[] = nodes.map((node) => {
      const text = node.textContent?.trim() || "";
      const base = node.id || slugify(text);
      let id = base;
      let i = 1;
      while (used.has(id)) id = `${base}-${i++}`;
      used.add(id);
      node.id = id;
      return { id, text, level: node.tagName === "H3" ? 3 : 2 };
    });

    setHeadings(collected);
    if (collected.length === 0) {
      setActiveId(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // activate a heading once it passes the fixed header, before it leaves up top
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [containerRef, contentKey]);

  return { headings, activeId };
}
